import React from 'react';

export const HOUSEHOLD_CHART_Y_AXIS_WIDTH = 180;
export const HOUSEHOLD_CHART_ROW_HEIGHT = 56;
const Y_LABEL_LINE_HEIGHT = 15;
const Y_LABEL_MAX_CHARS = 16;

/** Wrap long Y-axis names so parenthetical cashflows stay fully visible. */
export function wrapHouseholdChartYLabel(label: string, maxChars = Y_LABEL_MAX_CHARS): string[] {
  const text = label.trim();
  if (!text) return [];
  if (text.length <= maxChars) return [text];
  const paren = text.lastIndexOf(' (');
  if (paren > 0 && paren <= maxChars) {
    return [text.slice(0, paren), text.slice(paren + 1)];
  }
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (current && next.length > maxChars) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.slice(0, 3);
}

export function householdChartBarLabelText(
  count: number,
  amount: number,
  formatMoney: (n: number) => string
) {
  return `(${count} TX${count === 1 ? '' : 's'} · ${formatMoney(amount)})`;
}

type ChartBarRow = { name: string; count: number; value: number };

/** End-of-bar TX count and euro total, used at cashflow, group, and category drill. */
export function householdBarCountLabel(
  chartRows: ChartBarRow[],
  formatMoney: (n: number) => string,
  testIdPrefix: string
) {
  return (props: any) => {
    const box = props?.viewBox;
    const x = Number(box?.x ?? props?.x);
    const y = Number(box?.y ?? props?.y);
    const width = Number(box?.width ?? props?.width);
    const height = Number(box?.height ?? props?.height);
    const payloadName = String(props?.payload?.name || '').trim();
    const row =
      (payloadName && chartRows.find((item) => item.name === payloadName)) ||
      (typeof props.index === 'number' ? chartRows[props.index] : undefined);
    if (!row || ![x, y, width, height].every((n) => Number.isFinite(n))) {
      return null;
    }
    const right = Math.max(x, x + width);
    return (
      <text
        data-testid={`${testIdPrefix}-${row.name}`}
        x={right + 8}
        y={y + height / 2}
        fontSize={13}
        fill="currentColor"
        textAnchor="start"
        dominantBaseline="middle"
        style={{ pointerEvents: 'none', whiteSpace: 'nowrap' }}
      >
        {householdChartBarLabelText(row.count, row.value, formatMoney)}
      </text>
    );
  };
}

/** Clickable Y-axis category labels for household drill charts. */
export function householdChartYAxisTick(
  onNameClick: (name: string, event?: React.MouseEvent) => void,
  hitWidth = HOUSEHOLD_CHART_Y_AXIS_WIDTH
) {
  return (props: any) => {
    const label = String(props?.payload?.value ?? props?.value ?? '').trim();
    const x = Number(props?.x);
    const y = Number(props?.y);
    if (!label || !Number.isFinite(x) || !Number.isFinite(y)) {
      return <g />;
    }
    const lines = wrapHouseholdChartYLabel(label);
    const hitHeight = Math.max(32, lines.length * Y_LABEL_LINE_HEIGHT + 10);
    return (
      <g
        role="button"
        aria-label={label}
        data-testid={`household-chart-y-tick-${label}`}
        style={{ cursor: 'pointer', pointerEvents: 'all' }}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          event.preventDefault();
          onNameClick(label, event);
        }}
      >
        <rect
          x={x - hitWidth}
          y={y - hitHeight / 2}
          width={hitWidth}
          height={hitHeight}
          fill="transparent"
        />
        <text x={x} y={y} textAnchor="end" fontSize={13} fill="currentColor" dominantBaseline="middle">
          {lines.map((line, index) => (
            <tspan
              key={`${line}-${index}`}
              x={x}
              y={y}
              dy={(index - (lines.length - 1) / 2) * Y_LABEL_LINE_HEIGHT}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };
}
